// src/utils/fastUserRead.js
// The Fast Lane: the signed-in person's own record, read on a quick connection of its own.
//
// The problem, measured live in the owner's Chrome on 09/15/2026. Firestore's normal connection hands back a
// single-document read only once every read sharing that connection has caught up. When sign-in started the
// whole deals download (492 deals, about 30 MB) beside the person's own record, the record could not come back
// until every deal had arrived, so the plain "Loading..." screen covered the entire download (page frame at
// 3.3 s instead of 1.8 s). Moving the downloads to after the record fixed the frame (about 1.9 s) but pushed
// the numbers on screen 1.1-1.4 s later, because the downloads then started later.
//
// The fix the owner approved on 09/15/2026 ("Yes, Try the Fast Lane"): read that one record through
// firebase/firestore/lite, which sends a plain one-off request instead of joining the shared streaming
// connection. Nothing queues behind anything, so App.js can start the two big downloads at sign-in again AND
// still draw the page frame the moment the record lands.
//
// Four things keep this safe:
//   - It is never used under jest. The lite build has no disableNetwork, the one thing that stops the test
//     suite opening real connections (firebase/firebaseConfig.js), so a test must never reach it.
//   - Its own code is downloaded when the app opens, not at the moment the record is needed, and the read
//     never waits more than a moment for it (the section below). Downloading code is not a read.
//   - It is never used before sign-in. A read before sign-in once locked the whole team out, so the reader
//     runs only when Firebase already reports this very person signed in.
//   - Anything unexpected falls back to loadCurrentUserAtLaunch, today's reader, so no behaviour depends on
//     the Fast Lane working: a chunk that will not download, a token that is not ready, or any error that is
//     not "the server could not be reached".
//
// It answers in exactly the shape loadCurrentUserAtLaunch answers in, so App.js needs no new branch:
//   { user }                          the record, with the Gmail token merged in
//   { user: null }                    no users/{uid} record
//   { user: null, unreachable: true } the server could not be reached (shows the Can't Reach screen)
//
// Nothing here reads the record as a Firestore Timestamp (no .toDate(), no instanceof Timestamp), which is why
// the lite build's own Timestamp class is not a risk for this record.
import { app, auth, isTest } from "../firebase/firebaseConfig";
import { isUnreachable, loadCurrentUserAtLaunch } from "./firebasefunctions";

// ---------------------------------------------------------------------------
// The code itself, downloaded when the app opens rather than when the record is needed.
//
// Loaded with a dynamic import so the lite build (about 125 KB minified, 28 KB over the wire) lands in its own
// small chunk instead of the file every launch has to fetch. 09/16/2026 review: that chunk used to be fetched
// at the instant the record was needed, which is the critical path of the very read this file exists to speed
// up, and after every upload it is a cold fetch (the chunk's name changes each build) racing the 30 MB deals
// download that now starts at that same instant. On a weak phone connection it could cost more than the
// second the Fast Lane is meant to win, making the first launch after a release SLOWER than before. So App.js
// starts the download when the app opens, beside the page downloads (utils/lastRole.js), and the read below
// never waits long for it.
//
// Starting it at launch does NOT break the rule that nothing may be read before sign-in — a read before
// sign-in once locked the whole team out. Downloading code is not a read: fetching and running the lite build
// only registers it with Firebase. It opens no connection and reads no document; only getFirestore and getDoc
// speak to the server, and both stay below the sign-in gate in loadSignedInUserFast. The test "only fetches
// the code: it opens no connection and reads nothing" pins exactly that.

// How long the record read will wait for that download before giving up and using today's reader. A quarter
// of a second, chosen against what is at stake: the Fast Lane is worth 1.1-1.4 s of numbers on screen, so the
// worst this can cost is about a fifth of the win, while still covering the ordinary case where the launch
// download is all but finished when sign-in lands — and costing nothing at all when it has already arrived,
// which is a finished promise rather than a wait. Waiting longer would start eating the very win it protects;
// waiting much less would abandon a chunk that is milliseconds away.
export const LITE_CHUNK_BUDGET_MS = 250;

// One download per launch, remembered here the way a page's code is (utils/lazyPage.js). A download that will
// not arrive is forgotten, so a later attempt — the Can't Reach screen's Retry — can try again.
let liteDownload = null;
// The quick reader itself, the moment that download arrives, so isFastLaneReady below can answer without
// waiting on a promise. Only the shared download ever sets it: a caller's own stand-in (tests) speaks for
// itself, never for the real express lane.
let liteChunk = null;

// Never rejects: a chunk that will not download only means today's reader answers instead, so this hands back
// the module, or nothing.
const fetchQuietly = (importLite) =>
  Promise.resolve()
    .then(importLite)
    .then(
      (mod) => mod || null,
      () => null,
    );

// Started by App.js when the app opens, and again from the reader below in case it was not — a client's
// portal link starts no staff downloads at all, and a staff member may then sign in on that same page.
export const startFastLaneDownload = (importLite) => {
  // A caller with its own stand-in (tests) gets its own download and leaves the shared one alone.
  if (importLite) return fetchQuietly(importLite);
  // The real lite build is never downloaded under jest, for the reason at the top of this file.
  if (isTest || process.env.NODE_ENV === "test") return null;
  if (!liteDownload) {
    liteDownload = fetchQuietly(() => import("firebase/firestore/lite")).then((mod) => {
      if (!mod) liteDownload = null;
      else liteChunk = mod;
      return mod;
    });
  }
  return liteDownload;
};

// Is the express lane usable THIS launch? Asked by App.js before it starts anything, to choose the order of
// the launch (src/App.js).
//
// Why it exists (09/16/2026 review). The budget above stops the RECORD waiting for a chunk that has not
// arrived — but that is only half the launch. App.js starts the 30 MB of deals first BECAUSE the record is
// meant to travel on a lane of its own; if the chunk is missing, the record falls back to the shared
// connection those deals are already filling, which is the exact 09/15/2026 fault this file exists to avoid,
// and worse than the order it replaced. So the order is chosen per launch rather than assumed.
//
// Three things it must be:
//   - on the spot: a value, not a promise. Awaiting here would itself delay the very downloads being ordered.
//   - side-effect free: it starts no download and changes nothing, so asking is free and asking twice is the
//     same as asking once. The download is started when the app opens (utils/lastRole.js) and, failing that,
//     by the reader below.
//   - never yes under jest, where the real quick reader is never loaded at all (rule 3 at the top of this
//     file). A test therefore sees the safe order unless it says otherwise.
//
// Its failure direction is safe: answering "no" when the chunk had in fact just landed only means that one
// launch takes the older, slower-by-a-second order, never a broken or refused sign-in.
export const isFastLaneReady = () => {
  if (isTest || process.env.NODE_ENV === "test") return false;
  return liteChunk !== null;
};

// The module if the download finishes within the budget, otherwise nothing. The download itself is left
// running either way, so the next read finds it ready.
const withinBudget = (download, budgetMs) =>
  new Promise((resolve) => {
    let timer = null;
    let answered = false;
    const answer = (mod) => {
      if (answered) return;
      answered = true;
      clearTimeout(timer);
      resolve(mod || null);
    };
    timer = setTimeout(() => answer(null), budgetMs);
    download.then(answer, () => answer(null));
  });

// getFirestore(app) hands back the same instance every time, so there is nothing to remember for it. It is
// given the app that is ALREADY signed in: the lite build takes its credentials from that
// app's "auth-internal" provider (@firebase/firestore/dist/lite, LiteAuthCredentialsProvider), which
// firebase/auth fills in when firebaseConfig.js creates `auth` at load time. So the request carries the same
// sign-in as every other read, and nobody is ever asked to sign in twice. If that provider were somehow empty
// the request would go out signed-out and the rules would refuse it — which is exactly why a refusal falls
// back to today's reader rather than sending anyone to the login page.
export const loadSignedInUserFast = async (uid, options = {}) => {
  const {
    // The jest gate. The second half is belt and braces for a test that stands in for firebaseConfig and
    // leaves isTest out.
    underTest = isTest || process.env.NODE_ENV === "test",
    // Who Firebase says is signed in this instant. The Fast Lane only ever reads that person's own record.
    signedInUid = auth?.currentUser?.uid || null,
    // A test's own stand-in for the chunk. The app passes none and uses the download started at launch.
    importLite,
    budgetMs = LITE_CHUNK_BUDGET_MS,
  } = options;

  // Today's reader, unchanged, for everything the Fast Lane will not or cannot do.
  const fallback = () => loadCurrentUserAtLaunch(uid);

  if (!uid || underTest || signedInUid !== uid) return fallback();

  // Ordinarily this has been on its way since the app opened, so it is a promise that has already finished.
  // It is never waited on for long: if the chunk is not here yet, today's reader answers and the download
  // carries on for the next read.
  const download = startFastLaneDownload(importLite);
  const lite = download && (await withinBudget(download, budgetMs));
  if (!lite) return fallback();

  try {
    const db = lite.getFirestore(app);
    const [record, priv] = await Promise.all([
      lite.getDoc(lite.doc(db, "users", uid)).then(
        (snap) => ({ data: snap.exists() ? snap.data() : null }),
        (error) => ({ error }),
      ),
      // The Gmail token lives in a private subdoc. As in getUserGmailToken, a token that cannot be read is
      // simply "no token" — it never sinks the record.
      lite.getDoc(lite.doc(db, "users", uid, "private", "gmail")).then(
        (snap) => (snap.exists() ? snap.data()?.gmailtoken || null : null),
        () => null,
      ),
    ]);
    if (record.error) {
      if (isUnreachable(record.error)) return { user: null, unreachable: true };
      // Refused, or anything else unexpected: let today's reader answer, so the Fast Lane can never be the
      // reason somebody is turned away.
      return fallback();
    }
    if (!record.data) return { user: null };
    return { user: { ...record.data, gmailtoken: priv || record.data.gmailtoken || {} } };
  } catch (error) {
    if (isUnreachable(error)) return { user: null, unreachable: true };
    return fallback();
  }
};
